import { useState } from "react";

const T = {
  bg:"#211326", surface:"#211326", surfaceHi:"#2b1a33",
  border:"#3a2447", accent:"#E8DD02", accentDim:"rgba(232,221,2,0.1)",
  text:"#f0e9f4", muted:"#9a86a8", success:"#10b981", danger:"#ef4444",
};

const base = {
  fontFamily:"'Outfit',sans-serif", background:T.bg,
  minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center",
  color:T.text, padding:16,
};

const card = {
  background:T.surface, border:`1px solid ${T.border}`,
  borderRadius:16, padding:"36px 32px", width:"100%", maxWidth:420,
  boxShadow:"0 24px 80px rgba(0,0,0,0.6)",
};

const inputStyle = {
  width:"100%", padding:"10px 12px", borderRadius:8,
  background:T.bg, border:`1px solid ${T.border}`,
  color:T.text, fontSize:14, fontFamily:"inherit",
  outline:"none", boxSizing:"border-box",
};

const btnPrimary = {
  width:"100%", padding:"11px", borderRadius:8, border:"none",
  background:T.accent, color:T.bg, fontWeight:800, fontSize:15,
  cursor:"pointer", fontFamily:"inherit", marginTop:4,
};

const btnSecondary = {
  background:"none", border:"none", color:T.accent,
  cursor:"pointer", fontFamily:"inherit", fontSize:13, padding:0,
};

function Field({ label, type="text", value, onChange, placeholder, autoComplete }) {
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:T.muted, marginBottom:5 }}>{label}</div>
      <input type={type} value={value} onChange={e=>onChange(e.target.value)}
        placeholder={placeholder} autoComplete={autoComplete}
        style={inputStyle} />
    </div>
  );
}

function Logo() {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:28 }}>
      <svg width="32" height="32" viewBox="0 0 28 28">
        <rect width="28" height="28" rx="7" fill={T.accent}/>
        <path d="M7 20 L7 10 L20 10" stroke={T.bg} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="20" cy="10" r="2.5" fill={T.bg}/>
      </svg>
      <span style={{ display:"flex", flexDirection:"column", lineHeight:1.1 }}>
        <span style={{ fontWeight:800, fontSize:20, letterSpacing:"-0.5px" }}>FlashPro</span>
        <span style={{ fontSize:10, fontWeight:600, color:T.muted, letterSpacing:"0.04em" }}>developed by Flash Fold Limited</span>
      </span>
    </div>
  );
}

// ─── Login ────────────────────────────────────────────────────────────────────
export function LoginPage({ onLogin, onGoRegister }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setError(""); setLoading(true);
    try { await onLogin(email, password); }
    catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={base}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800;900&display=swap'); *{box-sizing:border-box;}`}</style>
      <div style={card}>
        <Logo />
        <div style={{ fontSize:22, fontWeight:800, marginBottom:4 }}>Welcome back</div>
        <div style={{ fontSize:13, color:T.muted, marginBottom:24 }}>Sign in to your FlashPro account</div>

        {error && <div style={{ background:"rgba(239,68,68,0.1)", border:`1px solid ${T.danger}`, borderRadius:8, padding:"10px 12px", color:T.danger, fontSize:13, marginBottom:16 }}>{error}</div>}

        <Field label="Email address" type="email" value={email} onChange={setEmail} placeholder="you@example.com" autoComplete="email" />
        <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" autoComplete="current-password" />

        <button style={{ ...btnPrimary, opacity:loading?0.7:1 }} onClick={submit} disabled={loading}>
          {loading ? "Signing in…" : "Sign In"}
        </button>

        <div style={{ marginTop:20, textAlign:"center", fontSize:13, color:T.muted }}>
          Don't have an account?{" "}
          <button style={btnSecondary} onClick={onGoRegister}>Request Access</button>
        </div>
        <div style={{ marginTop:18, textAlign:"center", fontSize:11, color:T.muted }}>
          © 2026 Flash Fold Limited · <a href="/privacy" target="_blank" rel="noreferrer" style={{ color:T.muted }}>Privacy</a> · <a href="/terms" target="_blank" rel="noreferrer" style={{ color:T.muted }}>Terms</a>
        </div>
      </div>
    </div>
  );
}

// ─── Register ─────────────────────────────────────────────────────────────────
export function RegisterPage({ onRegister, onGoLogin }) {
  const [fields, setFields] = useState({ name:"", email:"", password:"", confirm:"", company:"", phone:"", invoiceEmail:"" });
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = k => v => setFields(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!fields.name.trim()) return "Full name is required";
    if (!fields.company.trim()) return "Company name is required";
    if (!fields.invoiceEmail.trim()) return "Invoice email is required — where should we send your invoices?";
    if (!fields.invoiceEmail.includes("@")) return "That invoice email doesn\u2019t look right";
    /* ⚠️ Charl, 3 Oct: "change the form to ask for mobile number" - order and approval texts go to this number,
       so a landline (03...) would send them to the office. A NZ mobile: 02..., or +64 2... */
    if (!fields.phone.trim()) return "Mobile number is required";
    { const d = fields.phone.replace(/[^\d+]/g, "").replace(/^\+?64/, "0");
      if (!/^02\d{7,9}$/.test(d)) return "Please enter your MOBILE number (it starts with 02) - we text you when your order is approved or ready"; }
    if (!fields.email.includes("@")) return "Valid email is required";
    if (fields.password.length < 8) return "Password must be at least 8 characters";
    if (!/[A-Z]/.test(fields.password)) return "Password must contain an uppercase letter";
    if (!/[0-9]/.test(fields.password)) return "Password must contain a number";
    if (fields.password !== fields.confirm) return "Passwords do not match";
    if (!agree) return "You must agree to the Terms of Use and Privacy Policy";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(""); setLoading(true);
    try {
      await onRegister({ name:fields.name, email:fields.email, password:fields.password, company:fields.company, phone:fields.phone, invoiceEmail:fields.invoiceEmail.trim(), agreedTerms:true });
      setFields({ name:"", email:"", password:"", confirm:"", company:"", phone:"", invoiceEmail:"" });
      setSuccess(true);
      setTimeout(() => { try { onGoLogin(); } catch(e) {} }, 3000);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  if (success) return (
    <div style={base}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800;900&display=swap'); *{box-sizing:border-box;}`}</style>
      <div style={card}>
        <Logo />
        <div style={{ fontSize:36, marginBottom:12 }}>✅</div>
        <div style={{ fontSize:20, fontWeight:800, marginBottom:8 }}>Registration Submitted</div>
        <div style={{ fontSize:14, color:T.muted, lineHeight:1.7, marginBottom:24 }}>
          Your account request has been received. Our team will review and approve it shortly.<br/><br/>
          You'll receive an email confirmation once your account is active.<br/><br/>
          Taking you to sign in…
        </div>
        <button style={btnSecondary} onClick={onGoLogin}>← Back to Login</button>
      </div>
    </div>
  );

  return (
    <div style={{ ...base, alignItems:"flex-start", paddingTop:32 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800;900&display=swap'); *{box-sizing:border-box;}`}</style>
      <div style={{ ...card, maxWidth:440 }}>
        <Logo />
        <div style={{ fontSize:22, fontWeight:800, marginBottom:4 }}>Request Access</div>
        <div style={{ fontSize:13, color:T.muted, marginBottom:24 }}>Fill in your details — our team will approve your account.</div>

        {error && <div style={{ background:"rgba(239,68,68,0.1)", border:`1px solid ${T.danger}`, borderRadius:8, padding:"10px 12px", color:T.danger, fontSize:13, marginBottom:16 }}>{error}</div>}

        <Field label="Full Name *" value={fields.name} onChange={set("name")} placeholder="Jane Smith" />
        <Field label="Email Address *" type="email" value={fields.email} onChange={set("email")} placeholder="jane@smithroofing.com.au" autoComplete="email" />
        <Field label="Company *" value={fields.company} onChange={set("company")} placeholder="Smith Roofing Pty Ltd" />
        <Field label="Invoice Email * — where invoices should go" value={fields.invoiceEmail} onChange={set("invoiceEmail")} placeholder="accounts@smithroofing.co.nz" />
        <Field label="Mobile number *" type="tel" value={fields.phone} onChange={set("phone")} placeholder="021 123 4567" />
        <div style={{ fontSize: 11.5, color: "#6b5a78", margin: "-6px 0 10px" }}>We text this number when your order is approved or ready - not an office landline.</div>
        <Field label="Password *" type="password" value={fields.password} onChange={set("password")} placeholder="Min. 8 chars, 1 uppercase, 1 number" autoComplete="new-password" />
        <Field label="Confirm Password *" type="password" value={fields.confirm} onChange={set("confirm")} placeholder="Re-enter password" autoComplete="new-password" />

        <div style={{ fontSize:11, color:T.muted, marginBottom:12, lineHeight:1.6, padding:"8px 12px", background:T.accentDim, borderRadius:7, border:`1px solid ${T.accent}22` }}>
          🔒 Password requirements: 8+ characters, at least one uppercase letter and one number.
        </div>
        <label style={{ display:"flex", alignItems:"flex-start", gap:9, marginBottom:16, cursor:"pointer", fontSize:12, color:T.muted, lineHeight:1.6 }}>
          <input type="checkbox" checked={agree} onChange={e=>setAgree(e.target.checked)} style={{ marginTop:3 }} />
          <span>I agree to the <a href="/terms" target="_blank" rel="noreferrer" style={{ color:T.accent }}>Terms of Use</a> and <a href="/privacy" target="_blank" rel="noreferrer" style={{ color:T.accent }}>Privacy Policy</a> *</span>
        </label>

        <button style={{ ...btnPrimary, opacity:loading?0.7:1 }} onClick={submit} disabled={loading}>
          {loading ? "Submitting…" : "Submit Registration Request"}
        </button>

        <div style={{ marginTop:20, textAlign:"center", fontSize:13, color:T.muted }}>
          Already have an account?{" "}
          <button style={btnSecondary} onClick={onGoLogin}>Sign In</button>
        </div>
        <div style={{ marginTop:18, textAlign:"center", fontSize:11, color:T.muted }}>
          © 2026 Flash Fold Limited · <a href="/privacy" target="_blank" rel="noreferrer" style={{ color:T.muted }}>Privacy</a> · <a href="/terms" target="_blank" rel="noreferrer" style={{ color:T.muted }}>Terms</a>
        </div>
      </div>
    </div>
  );
}
